import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { projectApi } from "../../projects/api/project";
import { coamAreaApi, CoamAreaFeature } from "../../geospatial/api/coamArea";
import { useFilterContext } from "../../../shared/context/FilterContext";

export interface GeospatialProject {
  id: number;
  title: string;
  project_reference: string;
  description: string;
  project_scope: string;
  address: string;
  county: string;
  coordinates: {
    type: string;
    coordinates: [number, number];
  };
  latitude?: number;
  longitude?: number;
  programme: string;
  programme_id: number;
  network: string;
  region_id: number;
  site: string;
  site_id: number;
  workstream: string;
  phase_name: string;
  phase_template: string;
  project_status: string;
  project_budget: number;
  actual_spend: number | null;
  planned_start_date: string;
  planned_end_date: string;
  planned_commissioning_date: string;
  planned_close_up_date: string;
  phases: Array<{
    phase_id: number;
    name: string;
    project_id: number;
    display_order: number;
    status: string;
    percentage_complete: number;
  }>;
  progress: {
    total: number;
    percentage: number;
  };
  rag: {
    time: "green" | "red" | "amber";
    cost: "green" | "red" | "amber";
    quality: "green" | "red" | "amber";
    overall: "green" | "red" | "amber";
  };
  finance: {
    total_budget: number;
    total_spent: number;
    total_remaining: number;
  };
  is_fav: boolean;
}

export const useGeospatialProjectsData = () => {
  const { quickFilters, tempFilters, handleUpdateTempFilters, handleApplyFilters, handleResetFilters } = useFilterContext();
  const [networkBoundaries, setNetworkBoundaries] = useState<CoamAreaFeature[]>([]);

  const networkBoundariesMutation = useMutation({
    mutationFn: () => coamAreaApi.fetchCoamAreas(),
    onSuccess: (response) => {
      if (response.status && response.data?.features) {
        setNetworkBoundaries(response.data.features);
      }
    },
  });

  useEffect(() => {
    networkBoundariesMutation.mutate();
  }, []);

  const queryParams = useMemo(
    () => ({
      programme_id: quickFilters.programme?.length ? quickFilters.programme : undefined,
      region_id: quickFilters.region?.length ? quickFilters.region : undefined,
      site_id: quickFilters.site?.length ? quickFilters.site : undefined,
      limit: 500,
    }),
    [quickFilters]
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["geospatial-projects-map", queryParams],
    queryFn: () => projectApi.fetchProjects(queryParams),
  });

  const projects: GeospatialProject[] = useMemo(() => {
    if (!data?.data?.rows) return [];
    return data.data.rows.map((project: GeospatialProject) => ({
      ...project,
      latitude: project.coordinates?.coordinates?.[1],
      longitude: project.coordinates?.coordinates?.[0],
    }));
  }, [data]);

  const projectsWithLocation = useMemo(
    () =>
      projects.filter(
        (project) =>
          project.latitude !== undefined &&
          project.longitude !== undefined &&
          !isNaN(project.latitude) &&
          !isNaN(project.longitude)
      ),
    [projects]
  );

  const hasActiveFilters = 
    (quickFilters.programme?.length ?? 0) > 0 ||
    (quickFilters.region?.length ?? 0) > 0 ||
    (quickFilters.site?.length ?? 0) > 0;

  return {
    projects: projectsWithLocation,
    allProjects: projects,
    totalCount: data?.data?.pagination?.total || 0,
    isLoading: isLoading || networkBoundariesMutation.isPending,
    isError,
    error: error instanceof Error ? error.message : "Failed to fetch projects",
    filters: quickFilters,
    tempFilters,
    updateFilter: handleUpdateTempFilters,
    applyFilters: handleApplyFilters,
    resetFilters: handleResetFilters,
    hasActiveFilters,
    refetch,
    networkBoundaries,
  };
};
